/**
 * ShootPickerDialog.jsx — load a delivered HDPhotoHub shoot into the library.
 *
 * Nothing is copied to the portal: tiles use our cached 480px thumbnails, and the
 * originals stay on the delivery CDN until a slide cell or the export needs them.
 */

import { useMemo, useState } from 'react';
import { Dialog } from '@mui/material';
import { useHdphSites, useRefreshShoot } from '../../hooks/useRotoTube';
import { useMobile } from '../../hooks/useMobile';
import { loadShoot, useCarousel } from '../../hooks/useCarouselStore';
import {
  shootAsleep, shootDate, shootLabel, shootManageUrl, shootStills, shootThumbUrl,
} from '../../lib/carousel/hdphotohub.js';

// "RE: 1429 Shoreline Dr, Santa Barbara" and "1429 Shoreline Dr" share a street
// number and street word — enough to float a job's own shoot to the top.
function streetKey(text) {
  const m = String(text || '').match(/(\d+)\s+([A-Za-z]{3,})/);
  return m ? (m[1] + ' ' + m[2]).toLowerCase() : '';
}

function fmtDate(site) {
  const d = shootDate(site);
  return d ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '';
}

export default function ShootPickerDialog({ open, onClose, suggest }) {
  const { isMobile } = useMobile();
  const { data: sites, isLoading, error } = useHdphSites(open);
  const refresh = useRefreshShoot();
  const library = useCarousel((s) => s.library);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(null);
  const [checking, setChecking] = useState(null);
  const [notes, setNotes] = useState({});

  const loadedSids = useMemo(() => {
    const m = new Map();
    library.forEach((im) => { if (im.source === 'hdph') m.set(im.sid, (m.get(im.sid) || 0) + 1); });
    return m;
  }, [library]);

  const rows = useMemo(() => {
    const all = (sites || []).filter((s) => shootStills(s).length || shootAsleep(s));
    const needle = q.trim().toLowerCase();
    const found = needle
      ? all.filter((s) => (s.address + ' ' + s.city + ' ' + s.state).toLowerCase().includes(needle))
      : all;
    const key = streetKey(suggest);
    if (!key) return found;
    const hit = (s) => (streetKey(s.address) === key ? 0 : 1);
    return found.slice().sort((a, b) => hit(a) - hit(b));
  }, [sites, q, suggest]);

  // Re-reads one shoot from the delivery system. The portal cannot wake media —
  // this is what you press AFTER waking it in HDPhotoHub.
  const recheck = async (site) => {
    // Waking no longer changes how many photos we can show — they load either
    // way — so the signal is the asleep count falling, not the photo count
    // rising. Measuring the wrong one reported every successful wake as a
    // failure.
    const before = shootAsleep(site);
    setChecking(site.sid);
    try {
      const next = await refresh.mutateAsync(site.sid);
      const woke = before - (next ? shootAsleep(next) : before);
      setNotes((m) => ({
        ...m,
        [site.sid]: woke > 0
          ? woke + (woke === 1 ? ' photo woke up — lighter now' : ' photos woke up — lighter now')
          : 'Still asleep in HDPhotoHub — they load here anyway, just heavier',
      }));
    } catch {
      setNotes((m) => ({ ...m, [site.sid]: 'Could not re-read this shoot.' }));
    } finally {
      setChecking(null);
    }
  };

  const choose = async (site) => {
    setBusy(site.sid);
    try {
      await loadShoot(site);
      onClose();
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => { if (!busy) onClose(); }}
      fullScreen={isMobile}
      className="cc-modal"
      aria-labelledby="cc-shoots-title"
      slotProps={{
        paper: { className: 'cc-root export-sheet cc-shoots' + (isMobile ? ' is-phone' : '') },
        backdrop: { style: { background: 'rgba(12,12,14,.66)' } },
      }}
    >
      <div className="sheet-head">
        <h3 id="cc-shoots-title">Load a shoot</h3>
        <span className="hint">
          {isLoading ? 'Reading the delivery library…' : rows.length + ' shoots'}
        </span>
        <button type="button" className="btn tiny ghost" onClick={onClose} disabled={!!busy}>Close</button>
      </div>
      <div className="export-opts">
        <label className="search cc-shoot-search">
          <input
            type="search"
            value={q}
            placeholder="Find a shoot — address or city"
            onChange={(e) => setQ(e.target.value)}
          />
        </label>
      </div>
      <ol className="export-list">
        {error && <li className="sheet-none">Could not read the delivery library.</li>}
        {!isLoading && !rows.length && <li className="sheet-none">No shoots match.</li>}
        {rows.map((site) => {
          const n = shootStills(site).length;
          const asleep = shootAsleep(site);
          const wakeUrl = shootManageUrl(site);
          const already = loadedSids.get(site.sid) || 0;
          const note = notes[site.sid];
          return (
            <li className="xrow cc-shootrow" key={site.sid}>
              <span className="xthumb cc-shootthumb">
                {site.cover
                  ? <img src={shootThumbUrl(site.cover)} alt="" loading="lazy" />
                  : <span className="cc-nocover" />}
              </span>
              <div className="xinfo">
                <span className="xname">{shootLabel(site)}</span>
                <span className="xmeta lbl">
                  {fmtDate(site)} · {n} {n === 1 ? 'photo' : 'photos'}
                  {already ? ' · ' + already + ' loaded' : ''}
                </span>
                {asleep > 0 && (
                  <span className="cc-asleep lbl">
                    <span className="cc-zzz" aria-hidden="true" />
                    {asleep} asleep · loaded from originals
                  </span>
                )}
                {note && <span className="cc-rownote lbl">{note}</span>}
              </div>
              <span className="cc-shootbtns">
                {asleep > 0 && wakeUrl && (
                  <a
                    className="btn tiny ghost cc-wake"
                    href={wakeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Wake this shoot in HDPhotoHub — restores the light versions and the client-facing gallery"
                  >
                    Wake ↗
                  </a>
                )}
                {asleep > 0 && (
                  <button
                    type="button"
                    className="btn tiny ghost"
                    title="Re-read this shoot after waking its photos in HDPhotoHub"
                    disabled={!!busy || checking === site.sid}
                    onClick={() => recheck(site)}
                  >
                    {checking === site.sid ? 'Checking…' : 'Refresh'}
                  </button>
                )}
                <button
                  type="button"
                  className="btn tiny"
                  disabled={!!busy || !n}
                  title={n ? undefined : 'Every photo in this shoot is asleep'}
                  onClick={() => choose(site)}
                >
                  {busy === site.sid ? 'Loading…' : !n ? 'All asleep' : already ? 'Load again' : 'Load'}
                </button>
              </span>
            </li>
          );
        })}
      </ol>
      <div className="export-foot">
        <span className="hint">
          {rows.some((s2) => shootAsleep(s2))
            ? 'Sleeping photos load from their originals — slower, but nothing is missing. Wake opens the shoot in HDPhotoHub; Refresh re-reads it afterwards.'
            : 'Photos load from the delivery library — nothing is uploaded or copied.'}
        </span>
      </div>
    </Dialog>
  );
}
